import { memo, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, File, FileText, Folder, FolderOpen, GitCompareArrows, Search } from 'lucide-react';
import { parseUpdaterChangeReview, type UpdaterReviewFile } from '../../utils/updaterChangeReview';
import { cn } from '../../utils/cn';

const PAGE_SIZE = 100;
const pathOrder = new Intl.Collator(undefined, { numeric: true });
const actionNames: Record<string, string> = {
  add: 'Add', replace: 'Replace', remove: 'Remove', delete: 'Remove', keep: 'Preserve', backup: 'Backup',
  ignore: 'Ignore', exclude: 'Exclude', disable: 'Disable', apply: 'Template', restore: 'Restore',
  review: 'Review', directory: 'Directory', 'rollback-baseline': 'Rollback baseline', 'rollback-directory': 'Rollback directory',
};
const actionName = (action: string) => actionNames[action] ?? action.replace(/-/g, ' ');
const actionColor = (action: string) => ['add', 'restore'].includes(action) ? 'text-emerald-400 bg-emerald-500/10'
  : ['remove', 'delete', 'exclude', 'disable'].includes(action) ? 'text-red-400 bg-red-500/10'
    : ['replace', 'apply', 'review'].includes(action) ? 'text-amber-400 bg-amber-500/10'
      : 'text-sky-400 bg-sky-500/10';

interface DirectoryNode {
  path: string;
  name: string;
  children: Map<string, DirectoryNode>;
  file?: UpdaterReviewFile;
  count: number;
}
interface TreeRow { node: DirectoryNode; depth: number; folder: boolean }

function makeTree(files: UpdaterReviewFile[]): DirectoryNode {
  const root: DirectoryNode = { path: '', name: '', children: new Map(), count: 0 };
  for (const file of files) {
    const parts = file.path.replace(/\/$/, '').split('/');
    let parent = root;
    parent.count++;
    parts.forEach((name, index) => {
      let node = parent.children.get(name);
      if (!node) {
        node = { path: parts.slice(0, index + 1).join('/'), name, children: new Map(), count: 0 };
        parent.children.set(name, node);
      }
      node.count++;
      if (index === parts.length - 1) node.file = file;
      parent = node;
    });
  }
  return root;
}

function treeRows(root: DirectoryNode, expanded: Set<string>, searching: boolean): TreeRow[] {
  const rows: TreeRow[] = [];
  const sorted = (node: DirectoryNode) => [...node.children.values()].sort((a, b) =>
    Number(b.children.size > 0 || b.file?.directory === true) - Number(a.children.size > 0 || a.file?.directory === true)
    || pathOrder.compare(a.name, b.name));
  const stack = sorted(root).reverse().map(node => ({ node, depth: 0 }));
  while (stack.length) {
    const row = stack.pop()!;
    const folder = row.node.children.size > 0 || row.node.file?.directory === true;
    rows.push({ ...row, folder });
    if (folder && (searching || expanded.has(row.node.path))) {
      const children = sorted(row.node);
      // Large dimensions can have more children than JavaScript's argument limit.
      for (let index = children.length - 1; index >= 0; index--) stack.push({ node: children[index], depth: row.depth + 1 });
    }
  }
  return rows;
}

function ActionBadge({ action }: { action: string }) {
  return <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap', actionColor(action))}>{actionName(action)}</span>;
}

/** Browses the immutable review payload only; never reads or changes server files. */
function PackChangeReviewContent({ lines }: { lines: readonly string[] }) {
  const review = useMemo(() => parseUpdaterChangeReview(lines), [lines]);
  const [query, setQuery] = useState('');
  const [action, setAction] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selection, setSelection] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [raw, setRaw] = useState(false);
  const search = query.trim().toLowerCase();
  const filtered = useMemo(() => review.files.filter(file => file.entries.some(entry =>
    (!action || entry.action === action) && (!search || entry.line.toLowerCase().includes(search)))), [review, action, search]);
  const root = useMemo(() => makeTree(filtered), [filtered]);
  const rows = useMemo(() => treeRows(root, expanded, !!search), [root, expanded, search]);
  const selected = filtered.find(file => file.path.replace(/\/$/, '') === selection);
  const notes = review.notes.filter(note => !search || note.line.toLowerCase().includes(search));
  const rawLines = lines.filter(line => !search || line.toLowerCase().includes(search));
  const toggle = (path: string) => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(path)) next.delete(path); else next.add(path);
    return next;
  });

  if (!review.files.length) return <div className="space-y-2">
    {lines.length > 20 && <input className="glass-input w-full text-xs" aria-label="Search review details" placeholder="Find a setting or notice…" value={query} onChange={event => setQuery(event.target.value)} />}
    <div className="max-h-80 overflow-auto rounded-lg border border-glass-200/10 bg-black/10 p-3" tabIndex={0} aria-label="Full review details">
      <pre className="whitespace-pre-wrap break-words text-xs text-[var(--text-primary)] leading-relaxed">{rawLines.join('\n') || 'No matching review lines.'}</pre>
    </div>
  </div>;

  return <div className="overflow-hidden rounded-xl border border-glass-200/15 bg-[var(--bg-primary)]/60" aria-label="Change review">
    <div className="flex flex-wrap items-center gap-2 border-b border-glass-200/10 px-3 py-2.5">
      <GitCompareArrows className="w-4 h-4 text-accent-300" />
      <span className="text-xs font-semibold text-[var(--text-primary)]">Change review</span>
      <span className="text-xs text-[var(--text-muted)]">{review.files.length.toLocaleString()} paths</span>
      <div className="flex flex-wrap gap-1.5 ml-auto">{Object.entries(review.counts).map(([kind, count]) =>
        <span key={kind} className={cn('rounded px-1.5 py-0.5 text-[10px] tabular-nums', actionColor(kind))}>{actionName(kind)} {count.toLocaleString()}</span>)}</div>
    </div>
    <div className="flex flex-wrap gap-2 p-3 border-b border-glass-200/10">
      <label className="relative flex-1 min-w-0 basis-48"><Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[var(--text-muted)]" />
        <input className="glass-input w-full text-xs pl-8" aria-label="Search review details" placeholder="Find a path, action or setting…" value={query}
          onChange={event => { setQuery(event.target.value); setLimit(PAGE_SIZE); }} />
      </label>
      {!raw && review.files.length > 0 && <select className="glass-input text-xs max-w-full" aria-label="Filter file actions" value={action}
        onChange={event => { setAction(event.target.value); setLimit(PAGE_SIZE); }}>
        <option value="">All file actions</option>{Object.keys(review.counts).map(kind => <option key={kind} value={kind}>{actionName(kind)}</option>)}
      </select>}
      <button type="button" className="glass-button-sm text-xs" aria-pressed={raw} onClick={() => setRaw(value => !value)}>{raw ? 'Directory view' : 'Raw review'}</button>
    </div>
    {raw ? <div className="max-h-96 overflow-auto p-4" tabIndex={0} aria-label="Full review details">
      <pre className="whitespace-pre-wrap break-words text-xs font-mono leading-relaxed text-[var(--text-primary)]">{rawLines.join('\n') || 'No matching review lines.'}</pre>
    </div> : <div className={cn('grid min-w-0', review.files.length > 0 && 'md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]')}>
      {review.files.length > 0 && <nav className="min-w-0 border-b md:border-b-0 md:border-r border-glass-200/10" aria-label="Review file explorer">
        <div className="flex items-center justify-between px-3 py-2 text-[10px] text-[var(--text-muted)] uppercase tracking-wider border-b border-glass-200/10">
          <span>Files · {filtered.length.toLocaleString()}{filtered.length !== review.files.length && ` / ${review.files.length.toLocaleString()}`}</span>
          <button type="button" className="text-accent-300 normal-case tracking-normal hover:underline" disabled={!!search}
            onClick={() => { setExpanded(new Set()); setLimit(PAGE_SIZE); }}>Collapse folders</button>
        </div>
        <div className="max-h-64 md:h-96 md:max-h-96 overflow-auto py-1" tabIndex={0} aria-label="Review directories">
          <button type="button" aria-pressed={!selected} className={cn('w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-glass-200/5', !selected && 'bg-accent-500/10 text-accent-300')}
            onClick={() => setSelection(null)}><FileText className="w-4 h-4 shrink-0" />Plan overview<span className="ml-auto text-[10px] text-[var(--text-muted)]">{review.notes.filter(note => note.line.trim()).length}</span></button>
          {rows.slice(0, limit).map(({ node, depth, folder }) => {
            const open = !!search || expanded.has(node.path);
            const actions = [...new Set(node.file?.entries.map(entry => entry.action) ?? [])];
            return <div key={node.path} className={cn('flex items-center min-w-0 hover:bg-glass-200/5', selected?.path.replace(/\/$/, '') === node.path && 'bg-accent-500/10')} style={{ paddingLeft: 8 + Math.min(depth, 8) * 14 }}>
              {folder ? <button type="button" className="p-1.5 shrink-0 text-[var(--text-muted)]" aria-label={`${open ? 'Collapse' : 'Expand'} ${node.path}`} aria-expanded={open}
                disabled={!!search || !node.children.size} onClick={() => toggle(node.path)}>{open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</button> : <span className="w-6 shrink-0" />}
              <button type="button" title={node.path} aria-label={`${node.file ? 'Review' : 'Browse folder'} ${node.path}`}
                aria-pressed={node.file ? selected?.path.replace(/\/$/, '') === node.path : undefined}
                aria-expanded={!node.file && folder ? open : undefined}
                className="min-w-0 flex-1 flex items-center gap-2 pr-3 py-2 text-left text-xs text-[var(--text-primary)]"
                onClick={() => { if (node.file) setSelection(node.path); else if (!search) toggle(node.path); }}>
                {folder ? open ? <FolderOpen className="w-4 h-4 shrink-0 text-accent-300" /> : <Folder className="w-4 h-4 shrink-0 text-accent-300" /> : <File className="w-3.5 h-3.5 shrink-0 text-[var(--text-muted)]" />}
                <span className="truncate font-mono">{node.name}{node.file?.directory ? '/' : ''}</span>
                <span className="ml-auto flex shrink-0 gap-1">{actions.slice(0, 2).map(kind => <ActionBadge key={kind} action={kind} />)}{actions.length > 2 && <span className="text-[10px] text-[var(--text-muted)]">+{actions.length - 2}</span>}{folder && <span className="text-[10px] text-[var(--text-muted)] tabular-nums">{node.count}</span>}</span>
              </button>
            </div>;
          })}
          {!rows.length && <p className="p-3 text-xs text-[var(--text-muted)]">No paths match these filters. Check the overview for matching settings or notices.</p>}
          {rows.length > limit && <button type="button" className="glass-button-sm text-xs m-3" onClick={() => setLimit(value => value + PAGE_SIZE)}>Show next {Math.min(PAGE_SIZE, rows.length - limit)} entries ({rows.length - limit} remaining)</button>}
        </div>
      </nav>}
      <section className="min-w-0" aria-label={selected ? 'Selected path details' : 'Plan overview details'}>
        <div className="px-4 py-3 border-b border-glass-200/10">
          <p className="text-xs font-semibold text-[var(--text-primary)] break-all">{selected?.path ?? 'Plan overview'}</p>
          <p className="mt-1 text-[11px] text-[var(--text-muted)]">{selected ? 'Reported actions for this path. File contents are not included in the plan.' : 'Review the scope, settings and notices, then open folders to inspect individual paths.'}</p>
        </div>
        <div className="max-h-80 md:h-80 overflow-auto p-4 space-y-3" tabIndex={0}>
          {selected ? selected.entries.map(entry => <div key={entry.index} className="rounded-lg border border-glass-200/10 overflow-hidden">
            <div className="px-3 py-2 flex items-center gap-2 border-b border-glass-200/10"><ActionBadge action={entry.action} />{entry.change && <span className="text-[10px] text-amber-400">{entry.change.replace(/-/g, ' ')}</span>}<span className="ml-auto text-[10px] text-[var(--text-muted)]">Review line {entry.index + 1}</span></div>
            <pre className="p-3 whitespace-pre-wrap break-words text-xs font-mono leading-relaxed text-[var(--text-primary)]">{entry.line}</pre>
          </div>) : notes.filter(note => note.line.trim()).length ? notes.filter(note => note.line.trim()).map(note => <p key={note.index}
            className={cn('text-xs whitespace-pre-wrap break-words leading-relaxed', /^\s*(NOTICE|WARNING|EXCLUDE)/.test(note.line) ? 'text-amber-400 border-l-2 border-amber-400/50 pl-3' : 'text-[var(--text-secondary)]', note.section === 'amp' && 'font-mono bg-glass-200/5 p-2 rounded')}>
            {note.line}
          </p>) : <p className="text-xs text-[var(--text-muted)]">{search ? 'No overview notes match your search.' : 'Select a path to inspect its reported changes.'}</p>}
        </div>
      </section>
    </div>}
    <p className="px-3 py-2 border-t border-glass-200/10 text-[10px] text-[var(--text-muted)]">{lines.length.toLocaleString()} review lines · Counts describe listed actions. Filters only change the view; approval covers the entire plan.</p>
  </div>;
}

export const PackChangeReview = memo(PackChangeReviewContent);
